import { useState, useEffect, useRef } from "react"
import { Button } from "~/react/ui/button"
import { SheetHeader, SheetTitle, SheetFooter } from "~/react/ui/sheet"
import { Tabs, TabsList, TabsListItem, TabsTrigger, TabsContent } from "~/react/ui/tabs"
import { Icon } from "~/react/ui/icon"
import { Avatar, AvatarImage, AvatarFallback } from "~/react/ui/avatar"
import defaultLeadAvatarUrl from "~/images/default_lead_avatar.svg"
import { Tag } from "~/react/ui/tag"
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuAction } from "~/react/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogCloseButton,
  DialogBody,
  DialogFooter,
} from "~/react/ui/dialog"
import HealthTab from "./tabs/health_tab"
import EmailAccountUsageTab from "./tabs/email_account_usage_tab"
import EmailAccountProfileTab from "./tabs/email_account_profile_tab"
import WarmupTab from "./tabs/warmup_tab"
import { CredentialsModal } from "./credentials_modal"
import { EmailAccountHeadingDetails } from "./email_account_heading_details"
import { getCsrfToken } from "~/lib/utils"
import {
  email_account_path,
  subscription_email_account_path,
  bulk_email_accounts_path,
  checkout_upgrade_plan_path,
} from "~/lib/routes"
import { useHealthCheck } from "~/react/hooks/useHealthCheck"
import type { EmailAccount } from "~/react/components/email_accounts/email_accounts_filters"
import type {
  BillingInfo,
  CreditCardInfo,
  PricePerWarmup,
} from "~/react/components/email_accounts_center/add_email_account_slots_modal/constants"
import { Turbo } from "@hotwired/turbo-rails"
import { reconnectEmailAccount, deleteEmailAccount } from "~/react/components/email_accounts/actions"
import { Popover, PopoverTrigger, PopoverContent } from "~/react/ui/popover"

type EmailAccountSidepanelProps = {
  account: EmailAccount
  onAccountUpdated?: (updatedAccount: EmailAccount) => void
  isPremium?: boolean
  isWarmupEnabled?: boolean
  stripePublicKey?: string | null
  creditCardInfo?: CreditCardInfo | null
  cardholderName?: string | null
  billingInfo?: BillingInfo | null
  userEmail?: string | null
  pricePerWarmup: PricePerWarmup
}

export default function EmailAccountSidepanel({
  account,
  onAccountUpdated,
  isPremium = false,
  isWarmupEnabled = false,
  stripePublicKey,
  creditCardInfo,
  cardholderName,
  billingInfo,
  userEmail,
  pricePerWarmup,
}: EmailAccountSidepanelProps) {
  const [isSaving, setIsSaving] = useState(false)
  const [activeTab, setActiveTab] = useState("profile")
  const [currentAccount, setCurrentAccount] = useState(account)
  const [isCredentialsModalOpen, setIsCredentialsModalOpen] = useState(false)
  const [upgradePopoverOpen, setUpgradePopoverOpen] = useState(false)
  const [confirmDisconnectOpen, setConfirmDisconnectOpen] = useState(false)
  const [confirmCancelRenewalOpen, setConfirmCancelRenewalOpen] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const disconnectDialogRef = useRef<HTMLDialogElement>(null)
  const cancelRenewalDialogRef = useRef<HTMLDialogElement>(null)
  const isBulkEdit = Boolean(account._bulkEdit)
  const accountDomain = currentAccount.email.split("@")[1] ?? ""

  // Health check hook - watches account.health to auto-clear running state when ActionCable updates
  const { isRunning: isHealthCheckInProgress, triggerHealthCheck } = useHealthCheck({
    watchValue: currentAccount.health,
  })

  // Fetch fresh account data when the account prop changes (e.g., when sidepanel reopens)
  useEffect(() => {
    // Skip fetching for bulk edit mode
    if (account._bulkEdit) {
      setCurrentAccount(account)
      setActiveTab("profile")
      return
    }

    const fetchFreshAccount = async () => {
      if (account.id === null) return

      try {
        const response = await fetch(email_account_path(account.id, { format: "json" }), {
          headers: {
            Accept: "application/json",
          },
          credentials: "same-origin",
        })

        if (response.ok) {
          const freshAccount = await response.json()
          setCurrentAccount(freshAccount)
        } else {
          // Fallback to prop if fetch fails
          setCurrentAccount(account)
        }
      } catch (error) {
        // Fallback to prop if fetch fails
        setCurrentAccount(account)
      }
    }

    // Only fetch if account ID changed (sidepanel reopened with different account)
    if (account.id !== currentAccount.id) {
      fetchFreshAccount()
    } else {
      // If same account, still update in case data changed
      setCurrentAccount(account)
    }
  }, [account.id, currentAccount.id, account._bulkEdit])

  // Initialize form data with account data; kept in sync via effect below
  const [formData, setFormData] = useState({
    daily_limit: currentAccount.daily_messages_limit ?? currentAccount.limit ?? 0,
    default_account: currentAccount.default_account ?? false,
    sender_name: currentAccount.display_name || currentAccount.owner_name || currentAccount.email || "",
    signature: currentAccount.signature || "",
    managedMailbox: {
      first_name: currentAccount.managed_mailbox?.first_name || "",
      last_name: currentAccount.managed_mailbox?.last_name || "",
    },
  })

  useEffect(() => {
    if (currentAccount._bulkEdit) {
      setFormData({
        daily_limit: 0,
        default_account: false,
        sender_name: "",
        signature: "",
        managedMailbox: {
          first_name: "",
          last_name: "",
        },
      })
    } else {
      setFormData({
        daily_limit: currentAccount.daily_messages_limit ?? currentAccount.limit ?? 0,
        default_account: currentAccount.default_account ?? false,
        sender_name: currentAccount.display_name || currentAccount.owner_name || currentAccount.email || "",
        signature: currentAccount.signature || "",
        managedMailbox: {
          first_name: currentAccount.managed_mailbox?.first_name || "",
          last_name: currentAccount.managed_mailbox?.last_name || "",
        },
      })
    }
  }, [currentAccount])

  useEffect(() => {
    if (!isWarmupEnabled && activeTab === "warmup") {
      setActiveTab("profile")
    }
  }, [isWarmupEnabled, activeTab])

  // Handle dialog open/close for disconnect dialog
  useEffect(() => {
    if (confirmDisconnectOpen) {
      disconnectDialogRef.current?.showModal()
    } else {
      disconnectDialogRef.current?.close()
    }
  }, [confirmDisconnectOpen])

  // Handle dialog open/close for cancel renewal dialog
  useEffect(() => {
    if (confirmCancelRenewalOpen) {
      cancelRenewalDialogRef.current?.showModal()
    } else {
      cancelRenewalDialogRef.current?.close()
    }
  }, [confirmCancelRenewalOpen])

  const handleRunHealthCheck = () => {
    if (currentAccount._bulkEdit) {
      triggerHealthCheck({
        emailAccountIds: currentAccount._bulkEdit.accountIds,
      })
    } else {
      triggerHealthCheck({ emailAccountIds: [currentAccount.id] })
    }
  }

  const handleDisconnect = async () => {
    if (currentAccount._bulkEdit) return
    if (currentAccount.id === null) return

    try {
      setActionLoading(true)
      const response = await fetch(email_account_path(currentAccount.id), {
        method: "DELETE",
        headers: {
          Accept: "text/vnd.turbo-stream.html, application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
      })

      if (response.ok) {
        const contentType = response.headers.get("content-type")
        if (contentType?.includes("text/vnd.turbo-stream.html")) {
          const html = await response.text()
          Turbo.renderStreamMessage(html)
        }

        App.displayFlash(
          "success",
          i18n.t("js.email_accounts_center.bulk.disconnect_success", {
            count: 1,
          }),
        )

        // Notify parent to close sidepanel and refresh
        // The table will handle closing the sidepanel via ActionCable update
        if (onAccountUpdated) {
          onAccountUpdated({ ...currentAccount, status: "disconnected" })
        }
      } else {
        const error = await response.json().catch(() => ({ error: i18n.t("js.unknown_error") }))
        App?.displayFlash?.("error", error.error || i18n.t("js.email_accounts_center.bulk.disconnect_error"))
      }
    } catch (err) {
      App?.displayFlash?.("error", i18n.t("js.email_accounts_center.bulk.disconnect_error"))
    } finally {
      setActionLoading(false)
    }
  }

  const handleReconnect = async () => {
    await reconnectEmailAccount(currentAccount, {
      onSuccess: () => {
        if (onAccountUpdated) {
          onAccountUpdated({ ...currentAccount, status: "active" })
        }
      },
    })
  }

  const handleDelete = async () => {
    await deleteEmailAccount(currentAccount.id, {
      confirmKey: "js.email_accounts_center.actions.delete_confirm",
      successKey: "js.email_accounts_center.actions.delete_success",
      errorKey: "js.email_accounts_center.actions.delete_error",
    })
  }

  const handleCancelRenewal = async () => {
    if (currentAccount._bulkEdit) return
    if (!currentAccount.managed) return
    if (currentAccount.id === null) return

    try {
      setActionLoading(true)
      const response = await fetch(subscription_email_account_path(currentAccount.id), {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
      })

      if (response.ok) {
        App?.displayFlash?.(
          "success",
          i18n.t("js.email_accounts_center.bulk.cancel_renewal_success", {
            count: 1,
          }),
        )
      } else {
        const error = await response.json().catch(() => ({ error: i18n.t("js.unknown_error") }))
        App?.displayFlash?.("error", error.error || i18n.t("js.email_accounts_center.bulk.cancel_renewal_error"))
      }
    } catch {
      App?.displayFlash?.("error", i18n.t("js.email_accounts_center.bulk.cancel_renewal_error"))
    } finally {
      setActionLoading(false)
    }
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      // Handle bulk edit mode
      if (currentAccount._bulkEdit) {
        const bulkData: {
          ids: number[]
          email_account: Record<string, unknown>
        } = {
          ids: currentAccount._bulkEdit.accountIds,
          email_account: {},
        }

        if (formData.daily_limit > 0) {
          bulkData.email_account = {
            ...bulkData.email_account,
            daily_messages_limit: formData.daily_limit,
          }
        }

        if (currentAccount._bulkEdit.canEditSenderName && formData.sender_name) {
          bulkData.email_account = {
            ...bulkData.email_account,
            display_name: formData.sender_name,
          }
        }

        const response = await fetch(bulk_email_accounts_path(), {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRF-Token": getCsrfToken(),
          },
          credentials: "same-origin",
          body: JSON.stringify(bulkData),
        })

        if (!response.ok) {
          const error = await response.json().catch(() => ({ error: i18n.t("js.unknown_error") }))
          throw new Error(error.error || i18n.t("js.email_accounts_center.errors.save_failed"))
        }

        // Parse response to get updated_count
        const result = await response.json().catch(() => ({}))
        const updatedCount = result.updated_count || currentAccount._bulkEdit.accountIds.length

        // Notify parent component to refresh data
        if (onAccountUpdated) {
          onAccountUpdated(currentAccount)
        }

        App.displayFlash(
          "success",
          i18n.t("js.email_accounts_center.bulk.edit_success", {
            count: updatedCount,
          }),
        )

        return
      }

      // Handle individual account update
      if (currentAccount.id === null) return

      const formDataToSend = new FormData()
      formDataToSend.append("email_account[daily_messages_limit]", formData.daily_limit.toString())
      formDataToSend.append("email_account[default_account]", formData.default_account ? "1" : "0")
      formDataToSend.append("email_account[send_email_as]", currentAccount.email)

      // Only include display_name if not Outlook and if it's different from email
      if (currentAccount.provider !== "outlook" && formData.sender_name !== currentAccount.email) {
        formDataToSend.append("email_account[display_name]", formData.sender_name)
      }

      // Include signature for SMTP/IMAP and managed accounts
      if (currentAccount.provider === "smtp_imap" || currentAccount.managed) {
        formDataToSend.append("email_account[signature]", formData.signature || "")
      }

      // Include managed_mailbox params for managed accounts
      if (currentAccount.managed) {
        if (formData.managedMailbox?.first_name) {
          formDataToSend.append("managed_mailbox[first_name]", formData.managedMailbox.first_name)
        }
        if (formData.managedMailbox?.last_name) {
          formDataToSend.append("managed_mailbox[last_name]", formData.managedMailbox.last_name)
        }
      }

      const response = await fetch(email_account_path(currentAccount.id), {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
        body: formDataToSend,
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: i18n.t("js.unknown_error") }))
        throw new Error(error.error || i18n.t("js.email_accounts_center.errors.save_failed"))
      }

      // Use the response from the server
      const updatedAccount = await response.json()

      // Update local state immediately
      setCurrentAccount(updatedAccount)

      // Notify parent component
      if (updatedAccount && onAccountUpdated) {
        onAccountUpdated(updatedAccount)
      }

      App.displayFlash("success", i18n.t("js.email_accounts_center.email_account.profile.save_success"))
    } catch (error) {
      console.error("Save failed:", error)
      App.displayFlash(
        "error",
        error instanceof Error ? error.message : i18n.t("js.email_accounts_center.errors.save_failed"),
      )
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <article>
      <div className="lead-details">
        <SheetHeader className="p-0">
          <header className="lead-details__header">
            <Avatar size="xxl">
              <AvatarImage src={currentAccount.owner_avatar_url} alt="" />
              <AvatarFallback>
                <img src={defaultLeadAvatarUrl} alt="" />
              </AvatarFallback>
            </Avatar>
            <div className="lead-details__heading">
              <SheetTitle className="lead-details__name mb-0">{currentAccount.email}</SheetTitle>
              <EmailAccountHeadingDetails account={currentAccount} />
            </div>
          </header>
        </SheetHeader>

        {!isBulkEdit && (
          <>
            <div className="lead-details__actions mb-4 d-flex align-items-center gap-2">
              <Tag
                children={
                  currentAccount.status === "active"
                    ? i18n.t("js.email_accounts_center.status.active")
                    : currentAccount.status === "pending"
                      ? i18n.t("js.email_accounts_center.status.pending", {
                          default: "Pending",
                        })
                      : i18n.t("js.email_accounts_center.status.disconnected")
                }
                variant={
                  currentAccount.status === "active"
                    ? "success"
                    : currentAccount.status === "pending"
                      ? "warning"
                      : "danger"
                }
              />
              <Tag children={i18n.t(`js.email_accounts_center.provider.${currentAccount.provider}`) || "—"} />
            </div>

            <div className="lead-details__actions d-flex align-items-center gap-2">
              {isPremium ? (
                <Button
                  size="sm"
                  type="button"
                  onClick={handleRunHealthCheck}
                  disabled={isHealthCheckInProgress || currentAccount.status === "pending"}
                  data-loading={isHealthCheckInProgress ? "true" : "none"}
                >
                  <Icon icon="shield-check" variant="regular" size="sm" aria-hidden="true" />
                  {i18n.t("js.email_accounts_center.actions.run_health_check")}
                </Button>
              ) : (
                <Popover open={upgradePopoverOpen} onOpenChange={setUpgradePopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button size="sm" type="button">
                      <Icon icon="shield-check" variant="regular" size="sm" aria-hidden="true" />
                      {i18n.t("js.email_accounts_center.actions.run_health_check")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="popover-body">
                    <div className="premium-feature-popover">
                      <strong>{i18n.t("js.email_accounts_center.email_account.health.upgrade_title")}</strong>
                      <p className="text-sm">
                        {i18n.t("js.email_accounts_center.email_account.health.upgrade_description")}
                      </p>
                      <Button variant="primary" asChild fullWidth>
                        <a
                          href={checkout_upgrade_plan_path({
                            needed_credits: 1,
                            needed_level: 1,
                            operation: "health_report",
                            direct_route: true,
                            format: "turbo_stream",
                          })}
                          data-turbo-stream="true"
                        >
                          <Icon icon="arrow-alt-circle-up" size="sm" />
                          {i18n.t("js.email_accounts_center.email_account.health.upgrade_cta")}
                        </a>
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="default"
                    size="sm"
                    iconOnly
                    type="button"
                    aria-label={i18n.t("js.email_accounts_center.actions.more_actions_aria")}
                  >
                    <Icon icon="ellipsis-v" variant="regular" size="sm" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="dropdown-menu-end">
                  {currentAccount.status === "active" ? (
                    <DropdownMenuAction type="button" onClick={() => setConfirmDisconnectOpen(true)}>
                      {i18n.t("js.email_accounts_center.actions.disconnect")}
                    </DropdownMenuAction>
                  ) : (
                    <>
                      <DropdownMenuAction
                        type="button"
                        onClick={handleReconnect}
                        disabled={currentAccount.status === "pending"}
                      >
                        {i18n.t("js.email_accounts_center.actions.reconnect")}
                      </DropdownMenuAction>
                      <DropdownMenuAction
                        type="button"
                        onClick={handleDelete}
                        disabled={currentAccount.status === "pending"}
                      >
                        {i18n.t("js.email_accounts_center.actions.delete")}
                      </DropdownMenuAction>
                    </>
                  )}
                  {currentAccount.managed && (
                    <DropdownMenuAction
                      type="button"
                      onClick={() => setIsCredentialsModalOpen(true)}
                      disabled={currentAccount.status === "pending"}
                    >
                      {i18n.t("js.email_accounts_center.actions.credentials")}
                    </DropdownMenuAction>
                  )}
                  {currentAccount.status === "active" && currentAccount.managed && (
                    <DropdownMenuAction type="button" onClick={() => setConfirmCancelRenewalOpen(true)}>
                      {i18n.t("js.email_accounts_center.actions.cancel_renewal")}
                    </DropdownMenuAction>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </>
        )}
      </div>

      {isBulkEdit ? (
        <div className="py-6">
          <EmailAccountProfileTab account={currentAccount} formData={formData} setFormData={setFormData} />
        </div>
      ) : (
        <Tabs
          value={activeTab}
          onValueChange={(value) => {
            // Prevent switching to health tab for non-premium users
            if (value === "health" && !isPremium) {
              return
            }
            setActiveTab(value)
          }}
        >
          <TabsList color="lightergrey" size="md" className="pt-6 px-6">
            <TabsListItem>
              <TabsTrigger value="profile">
                {i18n.t("js.email_accounts_center.email_account.profile.title")}
              </TabsTrigger>
            </TabsListItem>
            {isPremium && (
              <TabsListItem>
                <TabsTrigger value="health">
                  {i18n.t("js.email_accounts_center.email_account.health.title")}
                </TabsTrigger>
              </TabsListItem>
            )}
            <TabsListItem>
              <TabsTrigger value="usage">{i18n.t("js.email_accounts_center.email_account.usage.title")}</TabsTrigger>
            </TabsListItem>
            {isWarmupEnabled && (
              <TabsListItem value="warmup">
                <TabsTrigger value="warmup">
                  {i18n.t("js.email_accounts_center.email_account.warmup.title")}
                </TabsTrigger>
              </TabsListItem>
            )}
          </TabsList>

          <TabsContent value="profile" className="p-6">
            <EmailAccountProfileTab account={currentAccount} formData={formData} setFormData={setFormData} />
          </TabsContent>

          <TabsContent value="usage" className="p-6">
            <EmailAccountUsageTab accountId={currentAccount.id} />
          </TabsContent>

          <TabsContent value="health" className="p-0">
            {accountDomain && (
              <HealthTab domain={accountDomain} health={currentAccount.health ?? null} isPremium={isPremium} />
            )}
          </TabsContent>

          {isWarmupEnabled && (
            <TabsContent value="warmup" className="p-6">
              <WarmupTab
                account={currentAccount}
                stripePublicKey={stripePublicKey}
                creditCardInfo={creditCardInfo}
                cardholderName={cardholderName}
                billingInfo={billingInfo}
                userEmail={userEmail}
                pricePerWarmup={pricePerWarmup}
              />
            </TabsContent>
          )}
        </Tabs>
      )}
      {activeTab === "profile" && (
        <SheetFooter>
          <Button
            variant="primary"
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            data-loading={isSaving ? "true" : "none"}
          >
            {i18n.t("js.email_accounts_center.email_account.profile.save")}
          </Button>
        </SheetFooter>
      )}
      <CredentialsModal
        emailAccountId={currentAccount.id}
        email={currentAccount.email}
        isOpen={isCredentialsModalOpen}
        onClose={() => setIsCredentialsModalOpen(false)}
      />
      {confirmDisconnectOpen && (
        <Dialog ref={disconnectDialogRef}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{i18n.t("js.email_accounts_center.actions.disconnect")}</DialogTitle>
              <DialogCloseButton onClick={() => setConfirmDisconnectOpen(false)} />
            </DialogHeader>
            <DialogBody>
              <p>
                {i18n.t("js.email_accounts_center.bulk.disconnect_confirm", {
                  count: 1,
                })}
              </p>
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirmDisconnectOpen(false)} disabled={actionLoading}>
                {i18n.t("js.cancel")}
              </Button>
              <Button
                variant="danger"
                onClick={async () => {
                  setConfirmDisconnectOpen(false)
                  await handleDisconnect()
                }}
                disabled={actionLoading}
                data-loading={actionLoading ? "true" : "none"}
              >
                {i18n.t("js.email_accounts_center.actions.disconnect")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {confirmCancelRenewalOpen && (
        <Dialog ref={cancelRenewalDialogRef}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{i18n.t("js.email_accounts_center.actions.cancel_renewal")}</DialogTitle>
              <DialogCloseButton onClick={() => setConfirmCancelRenewalOpen(false)} />
            </DialogHeader>
            <DialogBody>
              <p>
                {i18n.t("js.email_accounts_center.bulk.cancel_renewal_confirm", {
                  count: 1,
                })}
              </p>
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirmCancelRenewalOpen(false)} disabled={actionLoading}>
                {i18n.t("js.cancel")}
              </Button>
              <Button
                variant="danger"
                onClick={async () => {
                  setConfirmCancelRenewalOpen(false)
                  await handleCancelRenewal()
                }}
                disabled={actionLoading}
                data-loading={actionLoading ? "true" : "none"}
              >
                {i18n.t("js.email_accounts_center.actions.cancel_renewal")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </article>
  )
}
